import React, { useEffect, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CheckIcon from "@mui/icons-material/Check";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import LocalNavbar from "./LocalComponents/LocalNavbar";
import Eye from "../../../GlobalComponents/Eye";
import synnexLogo from "../../../Assets/Images/Illustrations/synnex-logo.png";
import copyIcon from "../../../Assets/Images/Icons/copy.svg";
import loadingGif from "../../../Assets/Images/Animations/loading-management-console.gif";
import expiredIllustration from "../../../Assets/Images/Illustrations/failure.svg";
import invalidIllustration from "../../../Assets/Images/Illustrations/delete-card-img.svg";
import styles from "./loginCredentialsTemporaryPasswordTemporaryPassword.module.scss";
import { cloudManagerApi } from "../../../Utils/API";
import { GET_ADMIN_PORTAL_CREDENTIALS, MARK_CREDENTIAL_USED } from "../../../Utils/GlobalConstants";

// API response shape: { username: string, password: string, url: string }
// `url` is a portal label string e.g. "Microsoft 365 Partner Portal" — not an href

const LoginCredentialsTemporaryPassword = () => {
  // Read the raw token from the URL query string without decoding + as space.
  // useSearchParams().get("token") auto-decodes + → space (URL form-encoding spec),
  // which corrupts Base64-encoded encrypted tokens that legitimately contain + characters.
  // By extracting from window.location.search directly we get the raw percent-encoded
  // value — + is preserved as-is and passed verbatim to the backend.
  const rawQuery = window.location.search;
  const tokenMatch = rawQuery.match(/[?&]token=([^&]*)/);
  const token = tokenMatch ? tokenMatch[1] : null;

  // "loading" | "credentials" | "expired" | "invalid"
  const [pageState, setPageState] = useState("loading");
  const [credentials, setCredentials] = useState(null);

  // Eye component expects: passwordType = ['password'|'text', ''|'hide-eye']
  const [passwordType, setPasswordType] = useState(["password", ""]);
  const [copiedField, setCopiedField] = useState(null); // "username" | "password"

  useEffect(() => {
    window.scrollTo(0, 0);

    if (!token) {
      setPageState("expired");
      return;
    }

    // GET /secureCredentials/{token}
    // 200 active:   { isActive: true,  username, password, url }  → show credentials
    // 200 inactive: { isActive: false, errorMessage: "..." }       → show expired
    // 400           (catch)                                        → show invalid
    // other errors  (catch)                                        → show expired (safe default)
    cloudManagerApi
      .get(GET_ADMIN_PORTAL_CREDENTIALS + token)
      .then((resp) => {
        const data = resp.data;
        if (data && data.isActive === false) {
          setPageState("expired");
        } else {
          setCredentials(data);
          setPageState("credentials");

          // ── Mark credential as used (one-time-view enforcement) ────────────
          // Fire-and-forget POST in background immediately after successful view.
          // Makes the link inaccessible to any subsequent visitor.
          // Uncomment when backend endpoint is ready:
          //
          cloudManagerApi
            .post(MARK_CREDENTIAL_USED, {
              token: token,
              username: data.username,
            })
            .catch(() => {
              // Silently ignore — marking failure should not affect the user's view
            });
        }
      })
      .catch((err) => {
        const status = err.response?.status;
        if (status === 400) {
          setPageState("invalid");
        } else {
          setPageState("expired");
        }
      });
  }, [token]);

  // Copy a credential value to the clipboard; show "Copied ✓" for 2s
  const handleCopy = (value, fieldKey) => {
    navigator.clipboard.writeText(value).then(() => {
      setCopiedField(fieldKey);
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  // ── Loading State ─────────────────────────────────────────────────────────
  const renderLoading = () => (
    <div className={styles["loading-container"]}>
      <img
        src={loadingGif}
        alt="Loading..."
        className={styles["loading-gif"]}
      />
    </div>
  );

  // ── Expired / Already Viewed State ────────────────────────────────────────
  const renderExpired = () => (
    <div className={styles["state-container"]}>
      <img
        src={expiredIllustration}
        alt="Link expired"
        className={styles["state-illustration"]}
      />
      <h2 className={styles["state-title"]}>Link Expired</h2>
      <p className={styles["state-body"]}>
        This account setup link is no longer valid. Links expire after{" "}
        <strong>24 hours</strong> or immediately after being viewed for the
        first time.
      </p>
      <p className={styles["state-body"]}>
        If you need to retrieve your credentials again, please contact your
        Synnex account manager or reach us at{" "}
        <a
          href="mailto:online-support@au.synnex-grp.com"
          className={styles["state-link"]}
        >
          online-support@au.synnex-grp.com
        </a>
        .
      </p>
    </div>
  );

  // ── Invalid Token State ───────────────────────────────────────────────────
  const renderInvalid = () => (
    <div className={styles["state-container"]}>
      <img
        src={invalidIllustration}
        alt="Invalid link"
        className={styles["state-illustration"]}
      />
      <h2 className={styles["state-title"]}>Invalid Link</h2>
      <p className={styles["state-body"]}>
        The account setup link you followed is not valid. This may happen if
        the link was copied incorrectly, is incomplete, or has been tampered
        with.
      </p>
      <p className={styles["state-body"]}>
        Please use the exact link sent to your registered email address. If
        you continue to experience issues, contact your Synnex account manager
        or reach us at{" "}
        <a
          href="mailto:online-support@au.synnex-grp.com"
          className={styles["state-link"]}
        >
          online-support@au.synnex-grp.com
        </a>
        .
      </p>
    </div>
  );

  // ── Credential Row ────────────────────────────────────────────────────────
  const CredentialRow = ({ label, value, fieldKey, isPassword = false }) => {
    const displayValue =
      isPassword && passwordType[0] === "password"
        ? "•".repeat(value.length)
        : value;

    return (
      <div className={styles["credential-row"]}>
        <span className={styles["credential-label"]}>{label}</span>
        <div className={styles["credential-value-wrap"]}>
          <span className={styles["credential-value"]}>{displayValue}</span>
          <div className={styles["credential-actions"]}>
            {isPassword && (
              <Eye
                passwordType={passwordType}
                setPasswordType={setPasswordType}
              />
            )}
            <button
              className={`${styles["copy-icon-btn"]} ${
                copiedField === fieldKey
                  ? styles["copy-icon-btn--copied"]
                  : ""
              }`}
              onClick={() => handleCopy(value, fieldKey)}
              title={`Copy ${label}`}
              aria-label={`Copy ${label} to clipboard`}
            >
              {copiedField === fieldKey ? (
                <CheckIcon style={{ fontSize: 18, color: "#1D874C" }} />
              ) : (
                <img
                  src={copyIcon}
                  alt="copy"
                  className={styles["copy-icon-img"]}
                />
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ── Credentials View ──────────────────────────────────────────────────────
  const renderCredentials = () => (
    <>
      {/* Hero */}
      <div className={styles["hero-section"]}>
        <img
          src={synnexLogo}
          alt="Synnex"
          className={styles["hero-logo"]}
        />
        <h1 className={styles["hero-title"]}>
          Login Credentials
        </h1>
        <p className={styles["hero-subtitle"]}>
          Your cloud account has been successfully created.
        </p>
      </div>

      {/* Credential Card */}
      <div className={styles["card"]}>
        {/* Card Header */}
        <div className={styles["card-header"]}>
          <LockOutlinedIcon className={styles["card-header-icon"]} />
          <span className={styles["card-header-title"]}>
            Account Credentials
          </span>
        </div>

        {/* Card Body */}
        <div className={styles["card-body"]}>
          <CredentialRow
            label="Username"
            value={credentials.username}
            fieldKey="username"
          />

          <div className={styles["credential-divider"]} />

          <CredentialRow
            label="Temporary Password"
            value={credentials.password}
            fieldKey="password"
            isPassword
          />

          <div className={styles["credential-divider"]} />

          {/* Partner Portal Link — `url` from API is the actual link href */}
          <div className={styles["credential-row"]}>
            <span className={styles["credential-label"]}>
              Login URL
            </span>
            <div className={`${styles["credential-value-wrap"]} ${styles["partner-link-btn-wrap"]}`}>
              {credentials.url ? (
                <a
                  href={credentials.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary d-inline-flex align-items-center gap-2"
                  title={credentials.url}
                  style={{ textDecoration: "none", fontSize: "0.875rem" }}
                >
                  <span className={styles["partner-link-url"]}>
                    {credentials.url}
                  </span>
                  <OpenInNewIcon style={{ fontSize: 16, color: "#fff" }} />
                </a>
              ) : (
                <span className={styles["partner-link-pending"]}>
                  Link will be available shortly
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Inline Note */}
        <div className={styles["expiry-note"]}>
          <span className={styles["expiry-note-label"]}>Note: </span>
          This link is valid for <strong>24 hours only</strong> or until
          viewed for the first time, whichever comes first. Please save your
          credentials in a secure location before this page expires.
        </div>
      </div>
    </>
  );

  // ── Main Render ───────────────────────────────────────────────────────────
  // LocalNavbar is always shown — regardless of authentication state.
  // Footer is NOT rendered here; App.jsx renders it globally for all routes.
  return (
    <div className={styles["page-wrapper"]}>
      {/* Local, self-contained navbar — Synnex logo + CMS background image */}
      <LocalNavbar />

      <div
        className={`${styles["page-content"]} ${
          pageState === "loading" ? styles["page-content--loading"] : ""
        }`}
      >
        <Container>
          <Row className="justify-content-center">
            <Col xs={12} sm={11} md={10} lg={8} xl={7}>
              {pageState === "loading" && renderLoading()}
              {pageState === "expired" && renderExpired()}
              {pageState === "invalid" && renderInvalid()}
              {pageState === "credentials" &&
                credentials &&
                renderCredentials()}
            </Col>
          </Row>
        </Container>
      </div>
    </div>
  );
};

export default LoginCredentialsTemporaryPassword;

